import {Injectable} from '@angular/core';
import {EMPTY, forkJoin, from, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {User} from '../../models/user';
import {AppConfigService} from '../../services/config.service';
import {LocalStorageService} from '../../services/local-storage.service';
import {UserService} from '../../services/user.service';
import {LoggerService} from '../logger/logger.service';
import {base64UrlStrToBuf} from './helpers/base64-url-str-to-buf';
import {bufToBase64UrlStr} from './helpers/buf-to-base64-url-str';

const WEB_AUTHN_LOCAL_STORAGE_KEY = 'web-authn';

type EncryptedLoginCred = string; // IV (Initial Vector, 16 bytes) + Encrypted login and password.

interface RegistrationAuthData {
    cred: PublicKeyCredential;
    loginCred: EncryptedLoginCred;
    key: ArrayBuffer;
}

interface StorableAuthData {
    id: string;
    loginCred: EncryptedLoginCred;
    key: string;
}

interface RestoredAuthData {
    rawId: BufferSource;
    loginCred: EncryptedLoginCred;
    key: ArrayBuffer;
}

export interface LoginCred {
    login: string;
    password: string;
}

type LoginCredArray = [string, string]; // login, password

interface EncryptionParams {
    iv: Uint8Array<ArrayBuffer>;
    key: CryptoKey;
    exportedKey: ArrayBuffer;
}

@Injectable()
export class WebAuthnService {

    private readonly rpId: string = undefined; // Defaults to the current origin
    private readonly rpName: string = this.getRpName();

    constructor(
        private localStorageService: LocalStorageService,
        private appConfigService: AppConfigService,
        private userService: UserService,
        private loggerService: LoggerService
    ) {}

    public isPlatformAuthenticatorAvailable(): Observable<boolean> {
        let result: Observable<boolean>;

        const isBrowserSupportsWebAuthn = (
            window?.PublicKeyCredential !== undefined &&
            typeof window.PublicKeyCredential === 'function'
        );

        if (!isBrowserSupportsWebAuthn) {
            result = of(false);
        } else {
            result = from(PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
        }

        return result;
    }

    public register(loginCred: LoginCred): Observable<boolean> {
        const doRegister = (user: User) => {

            /**
             * Delete prev. stored data, so that even if user cancels new credential creation,
             * we will not prompt him to auth with the prev. credential.
             */
            this.deleteStoredAuthData();

            return from(this.createEncryptionParams()).pipe(
                switchMap(encryptionParams => {
                    const credCreationOptions = this.createCredCreationOptions(user, encryptionParams.exportedKey);

                    const credPromise = navigator.credentials.create({
                        publicKey: credCreationOptions
                    }) as Promise<PublicKeyCredential>;

                    return forkJoin({
                        cred:               from(credPromise),
                        encryptedLoginCred: this.encryptLoginPass(loginCred, encryptionParams),
                        encryptionParams: of(encryptionParams)
                    });
                }),
                map(({cred, encryptedLoginCred, encryptionParams}) => {
                    this.storeAuthData({
                        cred,
                        loginCred: encryptedLoginCred,
                        key:       encryptionParams.exportedKey
                    });

                    return true;
                }),
                catchError(error => {
                    this.loggerService.warn('WebAuthn error: Unable to register', error);

                    return of(false);
                })
            );
        };

        const resolveUser = (): Observable<User> =>
            this.userService.user
                ? of(this.userService.user)
                : this.userService.fetchCurrentUser();

        return this.isPlatformAuthenticatorAvailable().pipe(
            switchMap(isSupported => isSupported
                ? resolveUser().pipe(
                    switchMap(user => doRegister(user))
                )
                : of(false)
            )
        );
    }

    public authenticate(): Observable<LoginCred | never> {
        const doAuthenticate = () => {
            let result: Observable<LoginCred>;
            const restoredAuthData = this.restoreAuthData();

            if (restoredAuthData) {
                const credReqOptions = this.createCredRequestOptions(restoredAuthData);

                const credPromise = navigator.credentials.get({
                    publicKey: credReqOptions
                }) as Promise<PublicKeyCredential>;

                result = from(credPromise).pipe(
                    switchMap(cred => {
                        const exportedKey = (cred.response as AuthenticatorAssertionResponse).userHandle

                            // TODO: Use stored key, if the Authenticator didn't store it in userHandle.
                            || restoredAuthData.key;

                        this.loggerService.log({
                            responseName: cred.response.constructor.name,
                            responseAssertion: cred.response instanceof AuthenticatorAssertionResponse,
                            responseAttestation: cred.response instanceof AuthenticatorAttestationResponse,
                            responseCommon: cred.response instanceof AuthenticatorResponse,
                            keys: exportedKey ? Object.keys(exportedKey) : '<falsy>',
                            typeof: typeof exportedKey,
                            name: exportedKey?.constructor.name,
                            value: exportedKey,
                            isFalsy: !exportedKey,
                            byteLength: exportedKey?.byteLength,
                            length: (exportedKey as any)?.length
                        }); // TODO

                        return this.decryptLoginPass(restoredAuthData.loginCred, exportedKey);
                    }),
                    catchError(error => {
                        this.loggerService.warn('WebAuthn error: Unable to authenticate', error);

                        return EMPTY;
                    })
                );
            }

            return result || EMPTY;
        };

        return this.hasCredentials()
            ? this.isPlatformAuthenticatorAvailable().pipe(
                switchMap(isSupported => isSupported
                    ? doAuthenticate()
                    : EMPTY
                )
            )
            : EMPTY;
    }

    private hasCredentials(): boolean {
        return !!this.restoreAuthData();
    }

    private createCredCreationOptions(user: User, exportedKey: ArrayBuffer): PublicKeyCredentialCreationOptions {
        const challenge = new Uint8Array(128);
        window.crypto.getRandomValues(challenge);

        return {
            challenge,
            rp:                     {
                id:   this.rpId,
                name: this.rpName
            },
            user:                   {
                id:          exportedKey, // It's used to encrypt/decrypt login/password.
                name:        user.login,
                displayName: user.fullName
            },
            pubKeyCredParams:       [
                {type: 'public-key', alg: -7},
                {type: 'public-key', alg: -257}
            ],
            attestation:            'none',
            authenticatorSelection: {
                authenticatorAttachment: 'platform',
                requireResidentKey:      false,
                userVerification:        'required'
            },
            timeout:                60000
        };
    }

    private createCredRequestOptions(restoredAuthData: RestoredAuthData): PublicKeyCredentialRequestOptions {
        const challenge = new Uint8Array(128);
        window.crypto.getRandomValues(challenge);

        return {
            challenge,
            rpId:             this.rpId,
            allowCredentials: [
                {
                    id:         restoredAuthData.rawId,
                    type:       'public-key',
                    transports: ['internal']
                }
            ],
            userVerification: 'required',
            timeout: 60000
        };
    }

    private storeAuthData(authData: RegistrationAuthData): void {
        const credential = authData.cred;

        const dataToStore: StorableAuthData = {
            id:        bufToBase64UrlStr(credential.rawId),
            loginCred: authData.loginCred,
            key:       bufToBase64UrlStr(authData.key)
        };

        this.localStorageService.set(WEB_AUTHN_LOCAL_STORAGE_KEY, dataToStore);
    }

    private deleteStoredAuthData(): void {
        this.localStorageService.remove(WEB_AUTHN_LOCAL_STORAGE_KEY);
    }

    private restoreAuthData(): RestoredAuthData {
        const storableAuthData: StorableAuthData = this.localStorageService.get(WEB_AUTHN_LOCAL_STORAGE_KEY, undefined);
        let restoredAuthData: RestoredAuthData;

        try {
            restoredAuthData = storableAuthData
                ? {
                    rawId:     base64UrlStrToBuf(storableAuthData.id),
                    loginCred: storableAuthData.loginCred,
                    key:       base64UrlStrToBuf(storableAuthData.key)
                }
                : undefined;
        } catch (e) {
            console.warn('Error during parsing of the stored WebAuthn credential.');
            console.log(e);
        }

        return restoredAuthData;
    }

    private getRpName(): string {
        const appConfig = this.appConfigService.appConfig;

        return `${appConfig.applicationTitle} (${appConfig.env})`;
    }

    private async createEncryptionParams(): Promise<EncryptionParams> {
        const iv = window.crypto.getRandomValues(new Uint8Array(16));
        const key = await window.crypto.subtle.generateKey(
            {
                name:   'AES-CBC',
                length: 256
            },
            true,
            ['encrypt', 'decrypt']
        );
        const exportedKey = await window.crypto.subtle.exportKey('raw', key);

        return {iv, key, exportedKey};
    }

    private encryptLoginPass(loginCred: LoginCred, encryptionParams: EncryptionParams): Observable<string> {
        const doEncrypt = async () => {

            const encoder = new TextEncoder();
            const loginCredArr: LoginCredArray = [loginCred.login, loginCred.password];
            const msg = encoder.encode(JSON.stringify(loginCredArr));

            const encryptedBuf = await window.crypto.subtle.encrypt(
                {
                    name: 'AES-CBC',
                    iv:   encryptionParams.iv
                },
                encryptionParams.key,
                msg
            );

            const ivAndEncryptedBuf = new Uint8Array(encryptionParams.iv.length + encryptedBuf.byteLength);
            ivAndEncryptedBuf.set(encryptionParams.iv, 0);
            ivAndEncryptedBuf.set(new Uint8Array(encryptedBuf), encryptionParams.iv.length);

            return bufToBase64UrlStr(ivAndEncryptedBuf);
        };

        return from(doEncrypt());
    }

    private decryptLoginPass(encryptedLoginCred: string, exportedKey: ArrayBuffer): Observable<LoginCred> {
        const doDecrypt = async (): Promise<LoginCred> => {
            const key = await window.crypto.subtle.importKey('raw', exportedKey, 'AES-CBC', false, ['decrypt']);

            const ivAndEncryptedBuf = base64UrlStrToBuf(encryptedLoginCred);
            const iv = new Uint8Array(ivAndEncryptedBuf, 0, 16);
            const encryptedBuf = new Uint8Array(ivAndEncryptedBuf, iv.length);
            const loginCredJsonBuf = await window.crypto.subtle.decrypt(
                {
                    name: 'AES-CBC',
                    iv
                },
                key,
                encryptedBuf
            );
            const decoder = new TextDecoder();
            const loginCredJson = decoder.decode(loginCredJsonBuf);
            const loginCredArr: LoginCredArray = JSON.parse(loginCredJson);
            const [login, password] = loginCredArr;

            return {login, password};
        };

        return from(doDecrypt());
    }

}
